import { memo, useState, useEffect } from 'react';
import { Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import {
  BloxCustomDataProviderType,
  EditorialAssetSearchResultItem,
} from 'utility/types/dataProvider';
import { Workflow, WorkflowProcess } from 'providers/contexts/extensions/Workflows';

type WorflowFieldProps = {
  record: EditorialAssetSearchResultItem;
};

const WorkflowField = memo(({ record }: WorflowFieldProps) => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const workflowId = record.workflow_id;
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [workflowProcess, setWorkflowProcess] = useState<WorkflowProcess | null>(null);
  const [wfProcessId, setWorkflowProcessId] = useState<string | null>(
    record.workflow_process_id || null,
  );

  // Fetch current workflow and process
  const {
    data: wfData,
    isPending: wfPending,
    error: wfError,
  } = useQuery({
    queryKey: ['searchFieldWorkflowById', workflowId],
    queryFn: async (): Promise<Workflow | null> => {
      if (!workflowId) return null;
      try {
        const result = await dataProvider.getWorkflowById({
          query: { workflow_id: String(workflowId) },
        });

        const data = result.data ?? null;
        setWorkflow(data);

        return data;
      } catch (error) {
        console.error('Error fetching workflow by id:', error);
        throw error as Error;
      }
    },
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!workflowId,
  });

  // Update workflow state when data is fetched
  useEffect(() => {
    if (!wfPending && !wfError && wfData) {
      setWorkflow(wfData);

      // Get current process
      const processes = wfData?.processes || [];
      if (
        !wfProcessId ||
        !processes.find((p: WorkflowProcess) => p?.id === wfProcessId)
      ) {
        const firstProcess = processes[0];
        setWorkflowProcess(firstProcess);
        setWorkflowProcessId(firstProcess?.id || null);
      } else {
        const currentProcess = processes.find(
          (p: WorkflowProcess) => p.id === wfProcessId,
        );
        setWorkflowProcess(currentProcess || null);
      }
    } else if (!wfPending && !wfData) {
      setWorkflow(null);
      setWorkflowProcess(null);
    }
  }, [wfPending, wfError, wfData, wfProcessId]);

  if (!workflow) {
    return <></>;
  }

  return (
    <Typography
      variant="caption"
      data-private={true}
      sx={{
        display: 'block',
      }}
    >
      {workflow.name}
      {workflowProcess?.name ? ` / ${workflowProcess?.name}` : ''}
    </Typography>
  );
});

export default WorkflowField;
